import getPageConfig from 'corp/lib/auth';
import React, { lazy, useRef, useEffect } from 'react';
import { BrowserRouter, Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { useUnleashBoolean } from 'react-scale/components/featureFlag/useUnleashFeatureFlag';
import { DEPLOYMENT } from '@scale/frontend-lib/constants';
import { DEPLOYMENTS } from '@scale/shared/environment';
import { ICorpUserJson } from '@scale/shared/types/user';
import { BrowserPaths } from '@scale/shared/corpSearch';
import { QueryParamProvider } from 'use-query-params';
import initDatadog, {
  rumStartView,
  rumSetUser,
  startSessionReplayRecording,
} from '@scale/frontend-lib/datadog';
import { isEqual } from 'lodash';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import NoSSR from '@material-ui/core/NoSsr';
import BaseLayout from './components/Layout/BaseLayout';
import CorpLayout from './components/Layout/CorpLayout';
import ImbeUtilsPage from './views/ImbeUtilsPage';
import BeeTTSUtilsPage from './views/BeeTTSUtilsPage';
import BeeRLHFUtilsPage from './views/BeeRLHFUtilsPage';
import ESCIUtilsPage from './views/ESCIUtilsPage';
import FlamingoFairUtilsPage from './views/FlamingoFairUtilsPage';
import RoadrunnerUtilsPage from './views/RoadrunnerUtilsPage';
import BulbasaurUtilsPage from './views/BulbasaurUtilsPage';
import CherryINFPage from './views/CherryINFPage';
import GuidedWorkflowEditor from './views/GuidedWorkflows/GuidedWorkflowEditor';
import { ApiFetchCachingContextProvider } from 'react-scale/corp/lib/ApiFetchCachingContext';
import graphqlClient from 'nucleus/lib/graphQL';
import { Provider } from 'urql';
import { parse } from 'query-string';
import TaskerJourneyPageWrapper from 'react-scale/corp/views/TaskerJourney/TaskerJourneyPage';
import SensitiveContentUtilsPage from 'react-scale/corp/views/SensitiveContentUtilsPage';
import { RedirectToGenAIOpsHub } from 'react-scale/corp/views/LLMControlCenter/LLMControlCenterPage';
import MaybeRedirectToVite from './components/MaybeRedirectToVite';
import { ThreadedInteractionDemo } from 'react-scale/components/scalers/tasks/ThreadedInteraction/demo';
import TaskerScreeningUtils from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningUtils';
import ExpertManagementPage from 'react-scale/components/ExpertManagement/ExpertManagementPage';
import GrowthCenterPage from 'react-scale/components/GrowthCenter/GrowthCenterPage';
import { redirectToMultistage } from 'react-scale/corp/components/Lookup/SensorFusionLookupObject';
import { LayoutWithAlert, DCT_DEPRECATION_BANNER_LIST } from './helpers';

const MagicBenchmarksPage = lazy(() => import('./views/magicBenchmarks/MagicBenchmarksPage'));
const RolloutListPage = lazy(() => import('./views/abtest/RolloutListPage'));
const GenAIOpsHub = lazy(() => import('./views/CorpProjectGroupManager/GenAIOpsHub'));
const GDCDashboardPage = lazy(() => import('./views/gdcDashboard/GDCDashboardPage'));
const GenAIImageDBDashboard = lazy(() => import('./views/GenAIImageDBDashboard'));
const MistRedirect = lazy(() => import('./views/MistRedirect'));
const TeamPortalRoot = lazy(() => import('./views/qm/team/TeamPortalRoot'));
const ExplainPage = lazy(() => import('./views/ExplainPage/ExplainPage'));

const LookupPage = lazy(() => import('./views/LookupPage'));
const SearchPage: React.ComponentType<any> = lazy(() => import('./views/SearchPage'));
const RedirectToNextJS = lazy(() => import('./views/RedirectToNextJS'));
const AuditPage = lazy(() => import('./views/scalers/AuditPage'));
const CoursesPage = lazy(() => import('./views/CoursesPage'));
const GenAICoursesPage = lazy(() => import('./views/GenAICoursesPage'));
const ProjectSecurityManagementPage = lazy(
  () => import('./views/projectRoles/ProjectSecurityManagementPage'),
);
const TaskerAccessApprovalsPage = lazy(() => import('./views/approvals/TaskerAccessApprovalsPage'));
const StaffingExceptionRequestsPage = lazy(
  () => import('./views/projectRoles/StaffingExceptionRequestsPage'),
);
const StaffingAuthorizationsPage = lazy(
  () => import('./views/projectRoles/StaffingAuthorizationsPage'),
);
const QuesterEmailPage = lazy(() => import('./views/questers/EmailPage'));
const NewPage = lazy(() => import('./views/scalers/NewPage'));
const EnforcementPage = lazy(() => import('./views/fraud/FraudPage'));
const FraudDeprecatedPage = lazy(() => import('./views/fraud/FraudDeprecatedPage'));
const ScenariosPage = lazy(() => import('./views/ScenariosPage'));
const LayoutTestPage = lazy(() => import('./views/LayoutTestPage'));
const TuneLintingPage = lazy(() => import('./views/TuneLintingPage'));
const NewProjectPage = lazy(() => import('./views/scalers/NewProjectPage'));
const RecordingPage = lazy(() => import('./views/scalers/RecordingPage'));
const RecordingViewPage = lazy(() => import('./views/scalers/RecordingViewPage'));
const NeedsCorpPage = lazy(() => import('./views/bpo/NeedsCorpPage'));
const AddCorpUserPage = lazy(() => import('./views/AddCorpUserPage'));
const CourseUtilsPage = lazy(() => import('./views/CourseUtilsPage'));
const ConsentFormPage = lazy(() => import('./views/ConsentFormPage'));
const RecallTasksPage = lazy(() => import('./views/bpo/RecallTasksPage'));
const TaskTypePage = lazy(() => import('./views/TaskTypePage'));
const DemoteScalersPage = lazy(() => import('./views/bpo/DemoteScalersPage'));
const AssignBenchmarkPage = lazy(() => import('./views/AssignBenchmarkPage'));
const TransformS3UrlPage = lazy(() => import('./views/eng/TransformS3UrlPage'));
const RandomConfigsPage = lazy(() => import('./views/eng/RandomConfigsPage'));
const AnnouncementsPage = lazy(() => import('./views/scalers/AnnouncementsPage'));
const SlackChannelsPage = lazy(() => import('./views/scalers/SlackChannelsPage'));
const LabelAssistantPage = lazy(() => import('./views/scalers/LabelAssistantPage'));
const QualificationsPage = lazy(() => import('./views/scalers/QualificationsPage'));
const TaskerAccountTestingPage = lazy(() => import('./views/scalers/TaskerAccountTestingPage'));
const MassPermissioningPage = lazy(() => import('./views/bpo/MassPermissioningPage'));
const UserActionsPage = lazy(() => import('./views/UserActionsPage'));
const TaskActionsPage = lazy(() => import('./views/TaskActionsPage'));
const PipelineStageActionsPage = lazy(() => import('./views/PipelineStageActionsPage'));
const BatchActionsPage = lazy(() => import('./views/BatchActionsPage'));
const PermissionsPage = lazy(() => import('./views/permissions/PermissionsPage'));
const IncreasePriorityPage = lazy(() => import('./views/IncreasePriorityPage'));
const QueuePriorityPage = lazy(() => import('./views/queue_priority/QueuePriorityPage'));
const UpdateInstructionsPage = lazy(() => import('./views/UpdateInstructionsPage'));
const ScaleLabsPage = lazy(() => import('./views/ScaleLabs/ScaleLabsPage'));
const ScaleLabsNewExperiment = lazy(() => import('./views/ScaleLabs/ScaleLabsNewExperiment'));
const ScaleLabsMonitorExperiments = lazy(
  () => import('./views/ScaleLabs/ScaleLabsMonitorExperiments'),
);
const ScaleLabsExperimentDetailsAndResultsPage = lazy(
  () => import('./views/ScaleLabs/pages/ScaleLabsExperimentDetailsAndResultsPage'),
);
const LidarLabsReroute = lazy(() => import('./views/ScaleLabs/LidarLabsReroute'));
const BenchmarkAuditPage = lazy(() => import('./views/BenchmarkAuditPage'));
const UndisablePage = lazy(() => import('./views/UndisablePage'));
const SlaPage = lazy(() => import('./views/sla/SlaPage'));
const SmeAuditPage = lazy(() => import('./views/sme_audit/SmeAuditPage'));
const SmeAuditBatchPage = lazy(() => import('./views/sme_audit/SmeAuditBatchPage'));
const SpeedAuditPage = lazy(() => import('./views/SpeedAuditPage'));
const SpeedAuditBatchPage = lazy(() => import('./views/speed_audit/SpeedAuditBatchPage'));
const SpeedAuditV2Page = lazy(() => import('./views/speed_audit/v2/SpeedAuditV2Page'));
const SpeedAuditV2BatchPage = lazy(() => import('./views/speed_audit/v2/SpeedAuditV2BatchPage'));
const UploadFilesToS3Page = lazy(() => import('./views/UploadFilesToS3Page'));
const UploadFilesToS3V2Page = lazy(() => import('./views/UploadFilesToS3PageV2'));
const UnexcludeWorkersPage = lazy(() => import('./views/UnexcludeWorkersPage'));
const UnassignBotClaimsPage = lazy(() => import('./views/UnassignBotClaimsPage'));
const CustomerDashboardPage = lazy(() => import('./views/CustomerDashboardPage'));
const DataEngineDashboardsPage = lazy(() => import('./views/DataEngineDashboardsPage'));
const CherryCatalogUtilsPage = lazy(() => import('./views/CherryCatalogUtilsPage'));
const MelonAudioUtilsPage = lazy(() => import('./views/MelonAudioUtilsPage'));
const MiniLidarAssignmentPage = lazy(() => import('./views/MiniLidarAssignmentPage'));
const TaskerLeaderboard = lazy(() => import('./views/TaskerLeaderboardPage'));
const ViewNERAuditErrorsPage = lazy(() => import('./views/ner/ViewNERAuditErrorsPage'));
const DemoPage = lazy(() => import('./views/DemoPage'));
const PipelineViewerPage = lazy(() => import('./views/demos/PipelineViewerPage'));
const FSSLPage = lazy(() => import('./views/demos/FewShotSiteLearn/index'));
const CatalogMLDemoPage = lazy(() => import('./views/demos/CatalogMLDemo/index'));
const TCGuidedWorkflowPage = lazy(() => import('./views/demos/TCGuidedWorkflow/index'));
const NiceConfigs = lazy(() => import('./components/NiceConfigs/index'));
const RewardsPage = lazy(() => import('./views/RewardsPage'));
const TwoDTasksPage = lazy(() => import('./views/2DTasksPage'));
const CatConsensusTweakerPage = lazy(() => import('./views/CatConsensusTweaker'));
const TwoDLintingPage = lazy(() => import('./views/2DLintingPage/ResponseLintersInfoPage'));
const CustomersAuditPage = lazy(() => import('./views/customers/CustomersAuditPage'));
const ProjectResponseViewerPage = lazy(() => import('./views/ProjectResponseViewerPage'));
const EditCorpPermissionRolesPage = lazy(() => import('./views/EditCorpPermissionRolesPage'));
const GPTDemoPage = lazy(() => import('./views/GPTDemoPage'));
const TxnDemoPage = lazy(() => import('./views/TxnDemoPage'));
const GPTSearchDemoPage = lazy(() => import('./views/GPTSearchDemoPage'));
const EmbeddingDemoPage = lazy(() => import('./views/EmbeddingDemoPage'));
const AudioDemoPage = lazy(() => import('./views/demos/AudioTranscription/AudioDemoPage'));
const SnowflakeStatusPage = lazy(() => import('./views/SnowflakeStatusPage'));
const OpsUtilsPage = lazy(() => import('./views/OpsUtils/OpsUtilsPage'));
const ProxyFetchPage = lazy(() => import('./views/ProxyFetchPage'));
const RapidPage = lazy(() => import('./views/rapid/RapidPage'));
const SupplyLeverDashPage = lazy(() => import('./views/SupplyLeverDashPage'));
const ScriptsPage = lazy(() => import('./views/ScriptsPage'));
const RecommendPage = lazy(() => import('./views/benchmarks/RecommendPage'));
const ParameterizePage = lazy(() => import('./views/benchmarks/ParameterizePage'));
const CreateParameterizedPage = lazy(() => import('./views/benchmarks/CreateParameterizedPage'));
const TaskAuditPage = lazy(() => import('./views/tasks/AuditPage'));
const SubtasksPage = lazy(() => import('./views/SubtasksPage'));
const V3TasksPage = lazy(() => import('./views/V3TasksPage'));
const V3TraitsPage = lazy(() => import('./views/V3TraitsPage'));
const TasksPage = lazy(() => import('./views/TasksPage'));
const TextCollectionDemoPage = lazy(() => import('./views/TextCollectionDemoPage'));
const GorillaMLConsensusDemoPage = lazy(() => import('./views/demos/GorillaMLConsensus/DemoPage'));
const TrendDetectionDemoPage = lazy(() => import('./views/demos/TrendDetection/DemoPage'));
const RobinMLDemoPage = lazy(() => import('./views/demos/RobinMLDemo/DemoPage'));
const YetiMLDemoPage = lazy(() => import('./views/demos/YetiMLDemo/DemoPage'));
const MLTranscriptionDemoPage = lazy(() => import('./views/demos/MLTranscriptionDemoPage'));
const TSModelPlaygroundPage = lazy(
  () => import('./views/demos/TSModelPlayground/TSModelPlayground/DemoPage'),
);
const EntertainmentAiPage = lazy(() => import('./views/demos/EntertainmentAi/EntertainmentAiPage'));
const LidarTasksPage = lazy(() => import('./views/LidarTasksPage'));
const LegoLidarTasksPage = lazy(() => import('./views/LegoLidarTasksPage'));
const LabelHintsPage = lazy(() => import('./views/projects/LabelHintsPage'));
const EditScenarioPage = lazy(() => import('./views/EditScenarioPage'));
const CreateScenarioPage = lazy(() => import('./views/CreateScenarioPage'));
const AutogenerateScenarioPage = lazy(
  () => import('./views/AutogenerateScenario/AutogenerateScenarioPage'),
);
const CompareAttemptsPage = lazy(() => import('./views/CompareAttemptsPage'));
const DynamicCorpPage = lazy(() => import('./views/DynamicCorpPage'));
const ProjectMetricsPage = lazy(() => import('./views/projects/ProjectMetricsPage'));
const BulkTagBenchmarksPage = lazy(() => import('./views/projects/BulkTagBenchmarksPage'));
const ImbeBulkTagBenchmarksPage = lazy(() => import('./views/projects/ImbeBulkTagBenchmarksPage'));
const ProjectBenchmarksPage = lazy(() => import('./views/projects/ProjectBenchmarksPage'));
const CategorizationHintsPage = lazy(() => import('./views/projects/CategorizationHintsPage'));
const BulkCreateBenchmarksPage = lazy(() => import('./views/projects/BulkCreateBenchmarksPage'));
const BatchDashboardPage = lazy(() => import('./views/stats/batch/BatchDashboardPage'));
const TaskerConfidenceStatsPage = lazy(() => import('./views/stats/TaskerConfidenceStatsPage'));
const ReviewerConfidenceStatsPage = lazy(() => import('./views/stats/ReviewerConfidenceStatsPage'));
const CategorizationThresholdsPage = lazy(
  () => import('./views/projects/CategorizationThresholdsPage'),
);
const LidarLiteLintingPage = lazy(
  () => import('./views/LidarliteLintingPage/LidarLiteLintingPage'),
);
const LinterConfigDocs = lazy(() => import('./views/linter_config_docs/LinterConfigDocs'));
const LinterMetricsPage = lazy(() => import('./views/LinterMetricsPage/LinterMetricsPage'));
const DisputeTaskPage = lazy(() => import('./views/dispute/DisputeTaskPage'));
const DisputeViewerPage = lazy(() => import('./views/dispute/DisputeViewerPage'));
const HomePage = lazy(() => import('./views/HomePage'));
const RemedialCoursesConfigPage = lazy(
  () => import('./views/remedial_courses/RemedialCoursesConfigPage'),
);
const BacktestPayConfigsPage = lazy(() => import('./views/backtest_pay/BacktestPayConfigsPage'));
const QualityLabPage = lazy(() => import('react-scale/corp/views/QualityLabPage'));
const QARPage = lazy(() => import('./views/QARPage'));
const RuleEnginePage = lazy(() => import('./views/RuleEnginePage'));
const QARSettingsPage = lazy(() => import('./views/QARSettingsPage'));
const QAHubPage = lazy(() => import('react-scale/corp/views/QAHubPage'));
const SpeedViewAuditsPage = lazy(() => import('./views/speed_view_audit'));
const ContractEditorPage = lazy(() => import('./views/contracts/ContractEditorPage'));
const GeocontextPage = lazy(() => import('../components/Map/GeocontextPage'));
const ListContractsPage = lazy(() => import('./views/contracts/ListContractsPage'));
const DocMetricsPage = lazy(() => import('./views/DocMetricsPage'));
const CorpJobsPage = lazy(() => import('./views/JobsPage'));
const CatalogGenerationAccessPage = lazy(
  () => import('./views/catalog_generation/CatalogGenerationAccessLinkPage'),
);
const CatalogForgeConfigPage = lazy(
  () => import('./views/catalog_generation/UpdateForgeConfigPage'),
);
const MiniLidarSubprojectsPage = lazy(() => import('./views/MiniLidarSubprojectsPage'));
const OntologyEditorPage = lazy(() => import('./views/OntologyEditorPage'));

const QuickLinterEditPage = lazy(
  () => import('./views/projects/quick_linters/QuickLinterEditPage'),
);

const QATeamDetailsPage = lazy(() => import('./views/QATeamDetailsPage'));

const PayoutJobsDashboard = lazy(() => import('./views/payoutJobs'));
const WorkerTeamsPage = lazy(() => import('./views/workerTeams/WorkerTeamsPage'));
const PdpQaDashboard = lazy(() => import('./views/pdp_qa_dashboard'));

const GisTaskPipelineToolPage = lazy(
  () => import('./views/gis_task_pipeline_tool/GisTaskPipelineTool'),
);
const GisJsonQuickEditorPage = lazy(
  () => import('./views/gisJsonQuickEditorTool/JsonQuickEditorPage'),
);

const GuidedWorkflowsPage = lazy(() => import('./views/GuidedWorkflows/GuidedWorkflowsPage'));
const GuidedWorkflowDetailsPage = lazy(
  () => import('./views/GuidedWorkflows/GuidedWorkflowDetailsPage/GuidedWorkflowDetailsPage'),
);

const ProjectGroupsPage = lazy(() => import('./views/DailyPlanning/ProjectGroups'));
const ProjectGroupPage = lazy(() => import('./views/DailyPlanning/ProjectGroups/ProjectGroupPage'));

const QueueHealthReportingPage = lazy(() => import('./views/DailyPlanning/QueueHealthReporting'));

const QueueMaster2 = lazy(() => import('./views/lidar_viz_dash/QueueMaster2'));

const ProductionMonitoringRoot = lazy(
  () => import('./views/production_monitoring/ProductionMonitoringRoot'),
);

const HelloWorldPage = lazy(() => import('./views/HelloWorldPage'));
const GPTSQL = lazy(() => import('./views/GPTSQL'));

const ActivityTrackerPage = lazy(() => import('./views/ActivityTrackerPage'));
const VlmPrelabelsWorkflowPage = lazy(() => import('./views/VlmPrelabelsWorkflowPage'));
const SimilarityScoresPage = lazy(() => import('./views/SimilarityScoresPage'));
const BatchSimilarityScoresPage = lazy(() => import('./views/BatchSimilarityScoresPage'));
const ClipPairMatcherPage = lazy(() => import('./views/ClipPairMatcherPage'));
const CopyAttemptToolPage = lazy(() => import('./views/CopyAttemptToolPage'));
const BulkCopyAttemptToolPage = lazy(() => import('./views/BulkCopyAttemptToolPage'));

const ApproveActionPage = lazy(() => import('./views/approvals/ApproveActionPage'));

const WorkLogApprovalsPage = lazy(() => import('./views/approvals/workLogApprovals'));

const ProjectBillingApprovalsPage = lazy(() => import('./views/approvals/projectBillingApprovals'));

const SignupFlowPage = lazy(() => import('./views/growth/SignupFlow'));

const ResumeCourseScreeningPage = lazy(() => import('./views/growth/ResumeCourseScreening'));

const SendEmailTemplatePage = lazy(() => import('./views/growth/SendEmailTemplate'));

const SLASim = lazy(() => import('./views/SLASim'));

const ProgressBarDemo = lazy(() => import('./views/ProgressBarDemo'));

const PipelineSpecialActionsPage = lazy(
  () => import('./views/pipeline_special_actions/PipelineSpecialActions'),
);

const BulkActionBatches = lazy(() => import('./views/bulk_action_batches/BulkActionBatchesPage'));

const BulkProjectConfigs = lazy(
  () => import('./views/bulk_project_configs/BulkProjectConfigsPage'),
);

const GisGlobalMapViewerPage = lazy(() => import('./views/gis_global_map/GlobalMap'));
const GisGlobalMapQuickEditor = lazy(() => import('./views/gis_global_quick_editor/QuickEditor'));

const CopyRemoveLintersForMultipleProjects = lazy(
  () => import('./views/CopyRemoveLintersPage/CopyRemoveLintersForMultipleProjects'),
);

const DolphinQAPage = lazy(() => import('./views/dolphin_qa/DolphinQAPage'));

const CatalogTaxonomyToolPage = lazy(() => import('./views/CatalogTaxonomyToolPage'));
const ChatTaskAdvancedAuditPage = lazy(() => import('./views/ChatTaskAdvancedAuditPage'));

const OnboardingRequirementPage = lazy(
  () => import('./views/onboardingBuddy/OnboardingRequirementPage'),
);
const OnboardingStageScreenPage = lazy(
  () => import('./views/onboardingBuddy/OnboardingStageScreenPage'),
);

const OnboardingProjectStagesPage = lazy(
  () => import('./views/onboardingBuddy/OnboardingProjectStagesPage'),
);

const LaunchDashboardPage = lazy(() => import('./views/launch/dashboard'));

const BulbaQuestionTypeProjectMappingPage = lazy(
  () => import('./views/bulba/BulbaQuestionTypeProjectMappingPage'),
);

const BulbaWorkerRecordUploadPage = lazy(() => import('./views/bulba/BulbaWorkerRecordUploadPage'));

const HubstaffPage = lazy(() => import('./views/scalers/HubstaffPage'));

const AudienceFormPage = lazy(() => import('./views/audience/AudienceForm'));

const AudienceListPage = lazy(() => import('./views/audience/AudienceList'));

const SpeedAuditBenchmarkPage = lazy(
  () => import('./views/SpeedAuditBenchmarkOperations/SpeedAuditBenchmarkOperationsPage'),
);
const CreateSpeedAuditBMPage = lazy(
  () => import('./views/SpeedAuditBenchmarkOperations/CreateSpeedAuditBenchmarkOperationPage'),
);

const NucleusProjectSyncPage = lazy(
  () => import('./views/nucleusProjectSync/NucleusProjectSyncPage'),
);

const MissionConfigurationFormPage = lazy(
  () => import('./views/missionConfig/MissionConfigFormPage'),
);

const MissionConfigurationListPage = lazy(() => import('./views/missionConfig/MissionConfigList'));

const MinimumWagesPage = lazy(() => import('./views/minimumWages/MinimumWagesPage'));
const PayStrataPermissionsPage = lazy(
  () => import('./views/strata/payStrataPermissions/PayStrataPermissionsPage'),
);
const PayTraitsPage = lazy(() => import('./views/strata/payTraits/PayTraitsPage'));
const PayTraitPage = lazy(() => import('./views/strata/payTraits/PayTraitPage'));
const PayBucketsPage = lazy(() => import('./views/strata/payBuckets/PayBucketsPage'));
const PayBucketPage = lazy(() => import('./views/strata/payBuckets/PayBucketPage'));
const PayConfigsPage = lazy(() => import('./views/strata/payConfigs/PayConfigsPage'));
const WorkerPayStrataPage = lazy(() => import('./views/strata/WorkerPayStrataPage'));
const ProjectStrataPage = lazy(() => import('./views/strata/ProjectStrataPage'));
const SubstagesPage = lazy(() => import('./views/sfu_substages/SubstagesPage'));

const WorkerSkillManagerPage = lazy(() => import('./views/WorkerSkillManagerPage'));
const WorkerTeamsManagerPage = lazy(() => import('./views/WorkerTeamsManagerPage'));

const ChatBulkAudit = lazy(() => import('./views/chatBulkAudit/ChatBulkAuditPage'));
const ChatDemoPage = lazy(() => import('./views/ChatDemo/ChatDemoPage'));
const ChatBulkAuditResults = lazy(() => import('./views/chatBulkAudit/ChatBulkAuditResultsPage'));

const SocialSentiment = lazy(() => import('./views/sentiment/SocialSentimentPage'));

const BulbaBugEditor = lazy(() => import('./views/bulba/BulbaBugEditor'));
const BulbaBugsPage = lazy(() => import('./views/bulba/BulbaBugsPage'));
const UserIdentity = lazy(() => import('./views/CorpProjectGroupManager/UserIdentity'));
const ProjectServingConfigPage = lazy(() => import('./views/ProjectServingConfigPage'));

const EnterpriseHubPage = lazy(() => import('./views/enterprise_hub/EnterpriseHubPage'));
const CreateCruiseControlTaskPage = lazy(() => import('./views/CreateCruiseControlTaskPage'));

interface CorpIndexProps {
  user: ICorpUserJson;
  config: $FIXME;
}

const CorpIndex: React.FC<CorpIndexProps> = ({ user, config }) => {
  const loggedUserIsAdmin = user?.accountRole === 'admin';
  useEffect(() => {
    rumSetUser(user);
  }, [user]);

  const prevMatch = useRef<ReturnType<typeof useRouteMatch> | null>(null);

  // Preload this so it's cached when we need it.
  useUnleashBoolean('redirect-to-opshub-on-vite', {}, { defaultValue: false });

  function createRUMRoute<T = any>(
    path: string | string[],
    renderElement: ((match: T) => React.ReactElement) | React.ReactElement,
    exact: boolean = false,
  ) {
    return (
      <Route
        path={path}
        exact={exact}
        render={({ match }) => {
          if (process.browser) {
            initDatadog({ version: process.env.BUILD_ID });
            startSessionReplayRecording();
          }
          if (!isEqual(match, prevMatch.current)) {
            prevMatch.current = match;
            rumStartView(path.toString());
          }
          const element =
            typeof renderElement === 'function'
              ? renderElement(match as T)
              : React.cloneElement(renderElement, { match });
          return element;
        }}
      />
    );
  }

  return (
    <NoSSR>
      <BrowserRouter basename="corp">
        <QueryParamProvider ReactRouterRoute={Route}>
          <BaseLayout>
            <UserIdentity />
            <Switch>
              {createRUMRoute(BrowserPaths.QM, <RedirectToNextJS />)}
              <Route path={BrowserPaths.QMWorkflow} component={RedirectToNextJS} />
              <MaybeRedirectToVite>
                <Switch>
                  <Route path={BrowserPaths.LayoutTest} component={LayoutTestPage} />

                  <Route exact path={BrowserPaths.TasksAudit}>
                    <TaskAuditPage user={user} config={config} />
                  </Route>

                  <Route path={BrowserPaths.Demo} component={DemoPage} />

                  <Route path={BrowserPaths.GptDemo} component={GPTDemoPage} />

                  <Route path={BrowserPaths.TxnDemo} component={TxnDemoPage} />

                  <Route path={BrowserPaths.GptDemoSearch} component={GPTSearchDemoPage} />

                  <Route path={BrowserPaths.Embedding} component={EmbeddingDemoPage} />

                  <Route path={BrowserPaths.EditScenario}>
                    <EditScenarioPage user={user} />
                  </Route>

                  <Route path={BrowserPaths.CreateScenario}>
                    <CreateScenarioPage userId={user._id} />
                  </Route>

                  <Route
                    path={BrowserPaths.TextCollectionDemo}
                    component={TextCollectionDemoPage}
                  />

                  <Route
                    path={BrowserPaths.MlHilCategorizationDemo}
                    component={GorillaMLConsensusDemoPage}
                  />

                  <Route
                    path={BrowserPaths.TrendDetectionDemo}
                    component={TrendDetectionDemoPage}
                  />

                  <Route path={BrowserPaths.RobinMLDemo} component={RobinMLDemoPage} />

                  <Route path={BrowserPaths.YetiMLDemo} component={YetiMLDemoPage} />

                  <Route
                    path={BrowserPaths.MlTranscriptionDemo}
                    component={MLTranscriptionDemoPage}
                  />

                  <Route path={BrowserPaths.TsModelPlayground}>
                    <TSModelPlaygroundPage user={user} />
                  </Route>

                  <Route path={'/chat_demo'}>
                    <ChatDemoPage />
                  </Route>

                  <Route path={BrowserPaths.EntertainmentAI}>
                    <EntertainmentAiPage />
                  </Route>

                  <Route path={BrowserPaths.LLM}>
                    <RedirectToGenAIOpsHub />
                  </Route>

                  <Route path={BrowserPaths.GenAIOpsHub}>
                    <GenAIOpsHub config={config} />
                  </Route>

                  <Route path={BrowserPaths.GDCDashboard}>
                    <GDCDashboardPage />
                  </Route>

                  <Route path={BrowserPaths.GenAIImageDBDashboard}>
                    <GenAIImageDBDashboard />
                  </Route>

                  <Route path={BrowserPaths.Mist}>
                    <MistRedirect />
                  </Route>

                  <Route path={BrowserPaths.ProjectResponseViewer}>
                    <ProjectResponseViewerPage config={config} />
                  </Route>

                  <Route
                    exact
                    path={BrowserPaths.DisputeViewer}
                    render={props => (
                      <DisputeViewerPage
                        params={props.match.params}
                        query={parse(props.location.search)}
                      />
                    )}
                  />

                  <Route path={BrowserPaths.AudioDemo} component={AudioDemoPage} />

                  <Route path={BrowserPaths.ProjectsFSSL} component={FSSLPage} />

                  <Route path={BrowserPaths.CatalogMLDemo} component={CatalogMLDemoPage} />

                  <Route path={BrowserPaths.ProjectsTCGWF} component={TCGuidedWorkflowPage} />

                  <Route path={BrowserPaths.MayforceChatDemo}>
                    <ThreadedInteractionDemo />
                  </Route>
                  <Redirect
                    from={BrowserPaths.MayforceExpertManagement}
                    to={BrowserPaths.ExpertManagement}
                  />
                  {createRUMRoute(BrowserPaths.ExpertManagement, <ExpertManagementPage />)}
                  {createRUMRoute(BrowserPaths.GrowthCenter, <GrowthCenterPage user={user} />)}
                  {createRUMRoute(BrowserPaths.ChatAdvancedAudit, <ChatTaskAdvancedAuditPage />)}

                  <Route path={BrowserPaths.Screening}>
                    <div style={{ padding: 24 }}>
                      <TaskerScreeningUtils user={user} />
                    </div>
                  </Route>

                  {/* diff types of configs */}
                  <Route exact path={BrowserPaths.NiceConfigs}>
                    <NiceConfigs user={user} type="default" />
                  </Route>
                  <Route path={BrowserPaths.NiceConfigsProject}>
                    <NiceConfigs user={user} type="project" />
                  </Route>
                  <Route path={BrowserPaths.NiceConfigsTaskType}>
                    <NiceConfigs user={user} type="taskType" />
                  </Route>
                  <Route path={BrowserPaths.NiceConfigsTaskCustomer}>
                    <NiceConfigs user={user} type="taskCustomer" />
                  </Route>
                  <Route path={BrowserPaths.NiceConfigsTaskCustomerAndType}>
                    <NiceConfigs user={user} type="taskCustomerAndType" />
                  </Route>
                  <Route path={BrowserPaths.GlobalMapViewer} component={GisGlobalMapViewerPage} />
                  <Route
                    path={BrowserPaths.GlobalMapQuickEditor}
                    component={GisGlobalMapQuickEditor}
                  />
                  <Route path={BrowserPaths.EnterpriseHub}>
                    <EnterpriseHubPage />
                  </Route>
                  <CorpLayout user={user}>
                    <Switch>
                      {/* ROUTES WITH BANNER ALERT */}
                      {/* <Route path={BrowserPaths.Rapid}>
                  <RapidPage user={user} />
                </Route> */}
                      {/* <Route path={BrowserPaths.AutoflagDashboard} component={AutoflagDashboardPage} /> */}
                      {/* <Route path={BrowserPaths.SFURedoRecall} component={SFURedoRecallWizardPage} /> */}
                      {/* <Route path={BrowserPaths.SFUTaskTracker} component={SFUTaskTracker} /> */}
                      <Route path={BrowserPaths.BulbaBugEditor} component={BulbaBugEditor} />
                      <Route path={BrowserPaths.ConsentForms}>
                        <ConsentFormPage user={user} />
                      </Route>
                      {/* <Route path={BrowserPaths.ScalersBan} component={BanPage} /> */}
                      <Route path={BrowserPaths.Root} exact component={HomePage} />
                      {createRUMRoute(BrowserPaths.Lookup, match => (
                        <LookupPage key={match.params.id} config={config} />
                      ))}
                      {createRUMRoute(BrowserPaths.Scripts, <ScriptsPage />)}
                      {createRUMRoute(BrowserPaths.Subtasks, <SubtasksPage />)}
                      {createRUMRoute(BrowserPaths.V3Tasks, <V3TasksPage />)}
                      {createRUMRoute(
                        BrowserPaths.V3Traits,
                        <V3TraitsPage hideDescription={false} />,
                      )}
                      {createRUMRoute(BrowserPaths.Tasks, <TasksPage />)}
                      <Redirect from={BrowserPaths.Script} to={BrowserPaths.LookupId} />
                      {createRUMRoute(BrowserPaths.DynamicCorp, <DynamicCorpPage user={user} />)}
                      {createRUMRoute(BrowserPaths.DCT, match =>
                        DCT_DEPRECATION_BANNER_LIST.includes(match.params.name) ? (
                          <LayoutWithAlert>
                            <DynamicCorpPage user={user} pageName={match.params.name} />
                          </LayoutWithAlert>
                        ) : (
                          <DynamicCorpPage user={user} pageName={match.params.name} />
                        ),
                      )}
                      {createRUMRoute(
                        BrowserPaths.Search,
                        <SearchPage config={config} loggedUserIsAdmin={loggedUserIsAdmin} />,
                      )}
                      <Route path={BrowserPaths.CompareAttempts}>
                        <CompareAttemptsPage config={config} />
                      </Route>
                      <Route path={BrowserPaths.EngTransformS3Url} component={TransformS3UrlPage} />
                      <Route path={BrowserPaths.EngRandomConfigs} component={RandomConfigsPage} />
                      {createRUMRoute(BrowserPaths.TaskActions, <TaskActionsPage />)}
                      {createRUMRoute(
                        BrowserPaths.PipelineStageActions,
                        <PipelineStageActionsPage />,
                      )}
                      {createRUMRoute(BrowserPaths.UserActions, <UserActionsPage />)}
                      {createRUMRoute(BrowserPaths.BatchActions, <BatchActionsPage />)}
                      <Route path={BrowserPaths.ScalersNew}>
                        <NewPage user={user} />
                      </Route>
                      <Route path={BrowserPaths.Enforcement}>
                        <EnforcementPage user={user} />
                      </Route>
                      <Route path={BrowserPaths.FraudDeprecated}>
                        <FraudDeprecatedPage />
                      </Route>
                      <Route path={BrowserPaths.QuestersEmail} component={QuesterEmailPage} />
                      <Route path={BrowserPaths.ScalersAudit} component={AuditPage} />
                      {createRUMRoute(
                        BrowserPaths.ScalersNewProject,
                        <NewProjectPage user={user} />,
                      )}
                      <Route path={BrowserPaths.ScalersAnnouncements}>
                        <AnnouncementsPage hideOutlier={false} />
                      </Route>
                      <Route
                        path={BrowserPaths.ScalersSlackChannels}
                        component={SlackChannelsPage}
                      />
                      <Route
                        path={BrowserPaths.ScalersLabelAssistant}
                        component={LabelAssistantPage}
                      />
                      <Route path={BrowserPaths.ScalersHubstaffPortal} component={HubstaffPage} />
                      <Route
                        path={BrowserPaths.ScalersQualifications}
                        component={QualificationsPage}
                      />
                      <Route path={BrowserPaths.ScalersTest}>
                        <TaskerAccountTestingPage user={user} />
                      </Route>
                      <Route path={BrowserPaths.ScalersRecording} component={RecordingViewPage} />
                      <Route path={BrowserPaths.ScalersRecordingBase} component={RecordingPage} />
                      <Route path={BrowserPaths.TaskTypeManager}>
                        <TaskTypePage user={user} />
                      </Route>
                      <Route
                        path={BrowserPaths.Rapid}
                        render={params =>
                          DCT_DEPRECATION_BANNER_LIST.includes(params.location.pathname) ? (
                            <LayoutWithAlert>
                              <RapidPage user={user} />
                            </LayoutWithAlert>
                          ) : (
                            <RapidPage user={user} />
                          )
                        }
                      />
                      <Route path={BrowserPaths.SLASim} component={SLASim} />
                      <Route path={BrowserPaths.StatsBatch} component={BatchDashboardPage} />
                      <Route
                        path={BrowserPaths.StatsTaskerConfidenceStats}
                        component={TaskerConfidenceStatsPage}
                      />
                      <Route
                        path={BrowserPaths.StatsReviewerConfidenceStats}
                        component={ReviewerConfidenceStatsPage}
                      />
                      {createRUMRoute(BrowserPaths.BPONeedsCorp, <NeedsCorpPage />)}
                      <Route path={BrowserPaths.BPORecallTasks} component={RecallTasksPage} />
                      <Route path={BrowserPaths.BPODemoteScalers} component={DemoteScalersPage} />
                      {createRUMRoute(BrowserPaths.BPOMassPermissioning, <MassPermissioningPage />)}
                      <Route path={BrowserPaths.ProjectsMetrics} component={ProjectMetricsPage} />
                      <Route path={BrowserPaths.ProjectsLabelHints} component={LabelHintsPage} />
                      <Route
                        path={BrowserPaths.ProjectsBenchmarks}
                        component={ProjectBenchmarksPage}
                      />
                      <Route
                        path={BrowserPaths.ProjectsBulkTagBenchmarks}
                        component={BulkTagBenchmarksPage}
                      />
                      <Route
                        path={BrowserPaths.ProjectsImbeBulkTagBenchmarks}
                        component={ImbeBulkTagBenchmarksPage}
                      />
                      <Route
                        path={BrowserPaths.ProjectsCategorizationHints}
                        component={CategorizationHintsPage}
                      />
                      <Route
                        path={BrowserPaths.ProjectsCategorizationThresholds}
                        component={CategorizationThresholdsPage}
                      />
                      {createRUMRoute(
                        BrowserPaths.ProjectsBulkCreateBenchmarks,
                        <BulkCreateBenchmarksPage />,
                      )}
                      <Route
                        exact
                        path={BrowserPaths.ProjectsQuickLintersNew}
                        component={QuickLinterEditPage}
                      />
                      <Route
                        exact
                        path={BrowserPaths.ProjectsQuickLintersEdit}
                        component={QuickLinterEditPage}
                      />
                      <Route
                        path={BrowserPaths.BenchmarksCreateParameterized}
                        component={CreateParameterizedPage}
                      />
                      <Route path={BrowserPaths.BenchmarksRecommend} component={RecommendPage} />
                      <Route
                        path={BrowserPaths.BenchmarksParameterize}
                        component={ParameterizePage}
                      />
                      {createRUMRoute(
                        BrowserPaths.Courses,
                        match => (
                          <CoursesPage match={match} />
                        ),
                        true,
                      )}
                      {createRUMRoute(BrowserPaths.GenAICourses, <GenAICoursesPage />, true)}
                      {createRUMRoute(
                        BrowserPaths.CoursesWithId,
                        match => (
                          <CoursesPage match={match} />
                        ),
                        true,
                      )}
                      {createRUMRoute(
                        BrowserPaths.ProjectRolesAuth,
                        <ProjectSecurityManagementPage user={user} />,
                        true,
                      )}
                      <Route exact path={BrowserPaths.MyApprovals}>
                        <TaskerAccessApprovalsPage user={user} />
                      </Route>
                      {createRUMRoute(
                        BrowserPaths.BenchmarkAuditOperation,
                        <SpeedAuditBenchmarkPage user={user} />,
                        true,
                      )}
                      {createRUMRoute(
                        BrowserPaths.BenchmarkAuditOperationNew,
                        <CreateSpeedAuditBMPage user={user} />,
                        true,
                      )}
                      <Route exact path={BrowserPaths.StaffingExceptionRequests}>
                        <StaffingExceptionRequestsPage />
                      </Route>
                      <Route exact path={BrowserPaths.StaffingAuthorizations}>
                        <StaffingAuthorizationsPage />
                      </Route>
                      <Route path={BrowserPaths.RemedialCourses}>
                        <RemedialCoursesConfigPage />
                      </Route>
                      <Route path={BrowserPaths.BacktestPay}>
                        <BacktestPayConfigsPage user={user} />
                      </Route>
                      <Route path={BrowserPaths.Geocontext} component={GeocontextPage} />
                      {createRUMRoute(BrowserPaths.SpeedViewAudits, <SpeedViewAuditsPage />)}
                      <Redirect
                        from={BrowserPaths.SpeedViewCustomerAudits}
                        to={BrowserPaths.SpeedViewAudits}
                      />
                      <Route path={BrowserPaths.CourseUtils} component={CourseUtilsPage} />
                      {createRUMRoute(BrowserPaths.Permissions, <PermissionsPage />)}
                      {createRUMRoute(BrowserPaths.AddCorpUser, <AddCorpUserPage />)}
                      <Route path={BrowserPaths.AssignBenchmark} component={AssignBenchmarkPage} />
                      <Route
                        path={BrowserPaths.IncreasePriority}
                        component={IncreasePriorityPage}
                      />
                      <Route path={BrowserPaths.QueuePriority} component={QueuePriorityPage} />
                      <Route
                        path={BrowserPaths.UpdateInstructions}
                        component={UpdateInstructionsPage}
                      />
                      <Route path={BrowserPaths.ScaleLabsNew} component={ScaleLabsNewExperiment} />
                      <Route
                        path={BrowserPaths.ScaleLabsMonitor}
                        component={ScaleLabsMonitorExperiments}
                      />
                      <Route path={BrowserPaths.ScaleLabs} component={ScaleLabsPage} />
                      <Route path={BrowserPaths.LidarLabs} component={LidarLabsReroute} />
                      <Route
                        path={BrowserPaths.ExperimentResults}
                        component={ScaleLabsExperimentDetailsAndResultsPage}
                      />
                      <Route
                        path={BrowserPaths.AutogenerateScenario}
                        component={AutogenerateScenarioPage}
                      />
                      {createRUMRoute(BrowserPaths.BenchmarkAudit, <BenchmarkAuditPage />)}
                      {createRUMRoute(BrowserPaths.UploadFilesToS3, <UploadFilesToS3Page />)}
                      {createRUMRoute(
                        BrowserPaths.V2UploadFilesToS3,
                        <UploadFilesToS3V2Page user={user} />,
                      )}
                      <Route
                        path={BrowserPaths.UnexcludeWorkers}
                        component={UnexcludeWorkersPage}
                      />
                      <Route path={BrowserPaths.TuneResponseLinters}>
                        <TuneLintingPage />
                      </Route>
                      <Route path={BrowserPaths.TuneResponseLintersBase}>
                        <TuneLintingPage />
                      </Route>
                      <Redirect
                        from={BrowserPaths.Tune2DLinting}
                        to={BrowserPaths.TuneResponseLintersBase}
                      />
                      <Route
                        path={BrowserPaths.CustomerDashboard}
                        component={CustomerDashboardPage}
                      />
                      <Route
                        path={BrowserPaths.DataEngineDashboards}
                        component={DataEngineDashboardsPage}
                      />
                      <Route
                        path={BrowserPaths.CherryCatalogUtils}
                        component={CherryCatalogUtilsPage}
                      />
                      <Route path={BrowserPaths.MelonAudioUtils} component={MelonAudioUtilsPage} />
                      <Route path={BrowserPaths.ImbeUtils} component={ImbeUtilsPage} />
                      <Route path={BrowserPaths.CherryInfUtils} component={CherryINFPage} />
                      <Route path={BrowserPaths.BeeTTSUtils}>
                        <BeeTTSUtilsPage user={user} />
                      </Route>
                      <Route path={BrowserPaths.BeeRLHFUtils}>
                        <BeeRLHFUtilsPage />
                      </Route>
                      <Route path={BrowserPaths.EsciUtils}>
                        <ESCIUtilsPage />
                      </Route>
                      <Route path={BrowserPaths.FlamingoFairUtils}>
                        <FlamingoFairUtilsPage />
                      </Route>
                      <Route path={BrowserPaths.RoadrunnerUtils}>
                        <RoadrunnerUtilsPage user={user} />
                      </Route>
                      <Route path={BrowserPaths.BulbasaurUtils}>
                        <BulbasaurUtilsPage user={user} />
                      </Route>

                      <Route path={BrowserPaths.SensitiveContentUtils}>
                        <SensitiveContentUtilsPage user={user} />
                      </Route>
                      <Route path={BrowserPaths.ResumeCourseScreening}>
                        <ResumeCourseScreeningPage user={user} />
                      </Route>
                      <Route path={BrowserPaths.SendEmailTemplate}>
                        <SendEmailTemplatePage />
                      </Route>
                      <Route path={BrowserPaths.TaskerLeaderboard} component={TaskerLeaderboard} />
                      <Route
                        path={BrowserPaths.MiniLidarAssignment}
                        component={MiniLidarAssignmentPage}
                      />
                      <Route
                        path={BrowserPaths.NERViewAuditErrors}
                        component={ViewNERAuditErrorsPage}
                      />
                      {createRUMRoute(BrowserPaths.UnassignBotClaims, <UnassignBotClaimsPage />)}
                      {createRUMRoute(BrowserPaths.Undisable, <UndisablePage />)}
                      {createRUMRoute(BrowserPaths.SLA, <SlaPage user={user} />)}
                      {createRUMRoute(BrowserPaths.Scenarios, <ScenariosPage />)}
                      {createRUMRoute(BrowserPaths.SmeAudit, <SmeAuditPage user={user} />, true)}
                      {createRUMRoute(
                        BrowserPaths.SmeAuditBatch,
                        <SmeAuditBatchPage user={user} />,
                        true,
                      )}
                      {createRUMRoute(
                        BrowserPaths.SpeedAudit,
                        <SpeedAuditPage user={user} />,
                        true,
                      )}
                      {createRUMRoute(
                        BrowserPaths.SpeedAuditBatch,
                        <SpeedAuditBatchPage user={user} />,
                      )}
                      {createRUMRoute(
                        BrowserPaths.V2SpeedAudit,
                        <SpeedAuditV2Page user={user} />,
                        true,
                      )}
                      {createRUMRoute(
                        BrowserPaths.V2SpeedAuditBatch,
                        <SpeedAuditV2BatchPage user={user} />,
                      )}
                      {createRUMRoute(
                        BrowserPaths.ChatBulkAuditResults,
                        <ChatBulkAuditResults user={user} />,
                      )}
                      {createRUMRoute(BrowserPaths.ChatBulkAudit, <ChatBulkAudit user={user} />)}
                      <Route
                        path={BrowserPaths.GISJSONQuickEditor}
                        component={GisJsonQuickEditorPage}
                      />
                      <Route path={BrowserPaths.OpsUtils} component={OpsUtilsPage} />
                      <Route path={BrowserPaths.ProxyFetch} component={ProxyFetchPage} />
                      {createRUMRoute(BrowserPaths.QAHub, <QAHubPage />)}
                      <Route path={BrowserPaths.QualityLab} component={QualityLabPage} />
                      <Route path={BrowserPaths.DolphinQA} component={DolphinQAPage}></Route>
                      <Route path={BrowserPaths.Rewards}>
                        <RewardsPage user={user} />
                      </Route>
                      <Route path={BrowserPaths.Tasks2D} component={TwoDTasksPage} />
                      <Route
                        path={BrowserPaths.CatConsensusTweaker}
                        component={CatConsensusTweakerPage}
                      />
                      <Route path={BrowserPaths.ResponseLintersInfo}>
                        <TwoDLintingPage />
                      </Route>
                      <Redirect
                        from={BrowserPaths.Linting2D}
                        to={BrowserPaths.ResponseLintersInfo}
                      />
                      <Route
                        path={BrowserPaths.LidarLiteLinting}
                        component={LidarLiteLintingPage}
                      />
                      <Route path={BrowserPaths.LinterConfigDocs} component={LinterConfigDocs} />
                      <Route path={BrowserPaths.LinterMetrics} component={LinterMetricsPage} />
                      <Route
                        path={BrowserPaths.DisputeTaskIdDisputeId}
                        render={props => (
                          <DisputeTaskPage
                            disputeId={props.match.params.disputeId}
                            taskId={props.match.params.taskId}
                          />
                        )}
                      />
                      <Route
                        path={BrowserPaths.DisputeTask}
                        render={props => <DisputeTaskPage taskId={props.match.params.taskId} />}
                      />
                      <Route path={BrowserPaths.SupplyLeverDash} component={SupplyLeverDashPage} />
                      <Route
                        path={BrowserPaths.EditCorpPermissionRoles}
                        component={EditCorpPermissionRolesPage}
                      />
                      <Route
                        path={BrowserPaths.ContractsContractEditor}
                        component={ContractEditorPage}
                      />
                      <Route
                        path={BrowserPaths.ContractsListContracts}
                        component={ListContractsPage}
                      />
                      {DEPLOYMENT === DEPLOYMENTS.COMMERCIAL && (
                        <Route
                          path={BrowserPaths.SnowflakeStatus}
                          component={SnowflakeStatusPage}
                        />
                      )}
                      <Route
                        path={BrowserPaths.NERViewAuditErrors}
                        component={ViewNERAuditErrorsPage}
                      />
                      <Route path={BrowserPaths.CustomersAudit}>
                        <CustomersAuditPage config={config} />
                      </Route>
                      <Route
                        path={BrowserPaths.DemosPipelineViewer}
                        component={PipelineViewerPage}
                      />

                      {createRUMRoute(BrowserPaths.Explain, <ExplainPage user={user} />)}
                      <Route path={BrowserPaths.TaskerJourney}>
                        <TaskerJourneyPageWrapper user={user} />
                      </Route>
                      <Route path={BrowserPaths.LidarTasks} component={LidarTasksPage} />
                      <Route path={BrowserPaths.LegoLidarTasks} component={LegoLidarTasksPage} />
                      <Route path={BrowserPaths.TeamTeamId}>
                        <ApiFetchCachingContextProvider>
                          <TeamPortalRoot user={user} />
                        </ApiFetchCachingContextProvider>
                      </Route>
                      <Redirect from={BrowserPaths.TeamsId} to={BrowserPaths.TeamId} />
                      <Route path={BrowserPaths.Teams} component={WorkerTeamsPage} />
                      <Route path={BrowserPaths.DocMetrics} component={DocMetricsPage} />
                      <Route path={BrowserPaths.Jobs}>
                        <CorpJobsPage user={user} query={{ performedBy: user._id }} />
                      </Route>
                      <Route
                        path={BrowserPaths.ForgeWaitList}
                        exact
                        component={CatalogGenerationAccessPage}
                      />
                      <Route
                        path={BrowserPaths.ForgeConfigs}
                        exact
                        component={CatalogForgeConfigPage}
                      />
                      <Route
                        path={BrowserPaths.MiniLidarCreateSubprojects}
                        component={MiniLidarSubprojectsPage}
                      />
                      <Route
                        path={BrowserPaths.GuidedWorkflowsEdit}
                        component={GuidedWorkflowEditor}
                      />
                      <Route
                        path={BrowserPaths.GuidedWorkflowsId}
                        component={GuidedWorkflowDetailsPage}
                      />
                      <Route path={BrowserPaths.GuidedWorkflows} component={GuidedWorkflowsPage} />
                      <Route
                        path={BrowserPaths.QueueHealthReporting}
                        component={QueueHealthReportingPage}
                      />
                      {createRUMRoute(BrowserPaths.ProjectGroups, <ProjectGroupsPage />)}
                      {createRUMRoute(
                        BrowserPaths.ProjectGroupProjectGroupId,
                        <ProjectGroupPage />,
                      )}
                      {createRUMRoute(BrowserPaths.ProjectGroup, <ProjectGroupPage />)}
                      <Route path={BrowserPaths.QATeamsTeamId} component={QATeamDetailsPage} />
                      <Route path={BrowserPaths.PayoutJobs} component={PayoutJobsDashboard} />
                      <Route path={BrowserPaths.PDPQADashboard} component={PdpQaDashboard} />
                      <Route
                        path={BrowserPaths.GISTaskPipelineTool}
                        component={GisTaskPipelineToolPage}
                      />
                      <Route path={BrowserPaths.OntologyId} component={OntologyEditorPage} />
                      <Route path={BrowserPaths.LidarVizDash} component={QueueMaster2} />
                      <Route path={BrowserPaths.ProductionMonitoring}>
                        <Provider value={graphqlClient}>
                          <ProductionMonitoringRoot />
                        </Provider>
                      </Route>
                      {createRUMRoute(
                        BrowserPaths.BulkActionBatches,
                        <BulkActionBatches user={user} />,
                      )}
                      {createRUMRoute(BrowserPaths.BulkProjectConfigs, <BulkProjectConfigs />)}
                      <Route
                        path={BrowserPaths.QARProjectId}
                        render={() => <QARPage user={user} />}
                      />
                      <Route path={BrowserPaths.QARSettings} component={QARSettingsPage} />
                      <Route path={BrowserPaths.Hello} component={HelloWorldPage} />
                      <Route
                        path={BrowserPaths.CreateCruiseControlTask}
                        component={CreateCruiseControlTaskPage}
                      />
                      <Route path={BrowserPaths.GPTSQL} component={GPTSQL} />
                      <Route path={BrowserPaths.ActivityTracker} component={ActivityTrackerPage} />
                      <Route
                        path={BrowserPaths.VlmPrelabelsWorkflow}
                        component={VlmPrelabelsWorkflowPage}
                      />
                      <Route
                        path={BrowserPaths.BatchSimilarityScoresPage}
                        component={BatchSimilarityScoresPage}
                      />
                      <Route
                        path={BrowserPaths.ClipPairMatcherPage}
                        component={ClipPairMatcherPage}
                      />
                      <Route
                        path={BrowserPaths.CopyAttemptToolPage}
                        component={CopyAttemptToolPage}
                      />
                      <Route
                        path={BrowserPaths.BulkCopyAttemptToolPage}
                        component={BulkCopyAttemptToolPage}
                      />
                      <Route
                        path={BrowserPaths.SimilarityScoresPage}
                        component={SimilarityScoresPage}
                      />
                      <Route path={BrowserPaths.SignupFlowLinkId} component={SignupFlowPage} />
                      <Route path={BrowserPaths.ReviewWorkLog} component={WorkLogApprovalsPage} />
                      <Route
                        path={BrowserPaths.ReviewProjectBilling}
                        component={ProjectBillingApprovalsPage}
                      />
                      <Route
                        path={BrowserPaths.ReviewActionId}
                        render={() => <ApproveActionPage user={user} />}
                      />
                      <Route path={BrowserPaths.ProgressBarDemo} component={ProgressBarDemo} />
                      {createRUMRoute(BrowserPaths.RuleEngine, <RuleEnginePage />)}
                      <Route
                        path={BrowserPaths.PipelineSpecialActions}
                        component={PipelineSpecialActionsPage}
                      />
                      <Route
                        path={BrowserPaths.CopyLintersFromMultipleProjects}
                        component={CopyRemoveLintersForMultipleProjects}
                      />
                      <Route
                        path={BrowserPaths.CatalogTaxonomyTool}
                        component={CatalogTaxonomyToolPage}
                      />
                      {createRUMRoute(
                        BrowserPaths.WorkerSkillManager,
                        <WorkerSkillManagerPage user={user} />,
                      )}
                      {createRUMRoute(
                        BrowserPaths.WorkerTeamsManager,
                        <ApiFetchCachingContextProvider>
                          <WorkerTeamsManagerPage user={user} />
                        </ApiFetchCachingContextProvider>,
                      )}
                      <Route
                        path={BrowserPaths.OnboardingRequirementId}
                        component={OnboardingRequirementPage}
                      />
                      <Route
                        path={BrowserPaths.OnboardingRequirementBase}
                        component={OnboardingRequirementPage}
                      />
                      <Route
                        path={BrowserPaths.OnboardingStageScreenId}
                        component={OnboardingStageScreenPage}
                      />
                      <Route
                        path={BrowserPaths.OnboardingStageScreenBase}
                        component={OnboardingStageScreenPage}
                      />
                      <Route
                        path={BrowserPaths.OnboardingProjectStagesId}
                        component={OnboardingProjectStagesPage}
                      />
                      <Route
                        path={BrowserPaths.OnboardingProjectStages}
                        component={OnboardingProjectStagesPage}
                      />
                      <Route path={BrowserPaths.LaunchEndpoints} component={LaunchDashboardPage} />
                      <Route
                        path={BrowserPaths.BulbaQuestionTypeProjectMapping}
                        component={BulbaQuestionTypeProjectMappingPage}
                      />
                      <Route
                        path={BrowserPaths.BulbaQuestionTypeProjectMappingBase}
                        component={BulbaQuestionTypeProjectMappingPage}
                      />
                      <Route path={BrowserPaths.StagedRollout} component={RolloutListPage} />
                      <Route
                        path={BrowserPaths.ProjectServingConfig}
                        component={ProjectServingConfigPage}
                      />
                      <Route
                        path={BrowserPaths.BulbaWorkerRecordUpload}
                        component={BulbaWorkerRecordUploadPage}
                      />
                      {createRUMRoute(BrowserPaths.AudienceNew, <AudienceFormPage />)}
                      {createRUMRoute(BrowserPaths.AudienceAudienceId, <AudienceFormPage />)}
                      {createRUMRoute(BrowserPaths.Audience, <AudienceListPage />)}
                      <Route
                        path={BrowserPaths.NucleusProjectSync}
                        component={NucleusProjectSyncPage}
                      />
                      {createRUMRoute(
                        BrowserPaths.MissionConfigNew,
                        <MissionConfigurationFormPage />,
                      )}
                      {createRUMRoute(
                        BrowserPaths.MissionConfigConfigId,
                        <MissionConfigurationFormPage />,
                      )}
                      {createRUMRoute(BrowserPaths.MissionConfig, <MissionConfigurationListPage />)}
                      {createRUMRoute(
                        BrowserPaths.PayStrataPermissions,
                        <PayStrataPermissionsPage user={user} />,
                      )}
                      {createRUMRoute(BrowserPaths.MinimumWages, <MinimumWagesPage />)}
                      {createRUMRoute(BrowserPaths.PayTraits, <PayTraitsPage />)}
                      {createRUMRoute(BrowserPaths.PayTraitId, <PayTraitPage />)}
                      {createRUMRoute(BrowserPaths.PayBuckets, <PayBucketsPage />)}
                      {createRUMRoute(BrowserPaths.PayBucketId, <PayBucketPage />)}
                      {createRUMRoute(BrowserPaths.PayConfigs, <PayConfigsPage />)}
                      {createRUMRoute(BrowserPaths.WorkerPayStrata, <WorkerPayStrataPage />)}
                      {createRUMRoute(BrowserPaths.ProjectStrata, <ProjectStrataPage />)}
                      {createRUMRoute(BrowserPaths.SFUSubstages, <SubstagesPage />)}
                      {createRUMRoute(BrowserPaths.VoiceOfTheContributor, <SocialSentiment />)}
                      {createRUMRoute(BrowserPaths.BulbaBugs, <BulbaBugsPage />)}
                      <Route
                        path={BrowserPaths.MultistageTask}
                        component={() => {
                          redirectToMultistage('task');
                          return null;
                        }}
                      />
                      <Route
                        path={BrowserPaths.MultistageProject}
                        component={() => {
                          redirectToMultistage('project');
                          return null;
                        }}
                      />
                      <Route
                        path={BrowserPaths.MultistageStageInstance}
                        component={() => {
                          redirectToMultistage('stage-instance');
                          return null;
                        }}
                      />
                      <Route path={BrowserPaths.MagicBenchmarks} component={MagicBenchmarksPage} />
                    </Switch>
                  </CorpLayout>
                </Switch>
              </MaybeRedirectToVite>
            </Switch>
          </BaseLayout>
        </QueryParamProvider>
      </BrowserRouter>
    </NoSSR>
  );
};

// @ts-ignore
CorpIndex.getPageConfig = getPageConfig;

export default CorpIndex;
